<template>
    <!-- Manage files: add answer sheets to an existing paper, or remove what's there -->
    <Dialog v-model:visible="manageDialogVisible" modal :header="managedPaper?.name || 'Manage files'"
        class="papers-dialog" :style="{ width: '48rem', maxWidth: '95vw' }">
        <div v-if="managedPaper" class="manage-body">
            <div class="paper-meta manage-meta">
                <span class="meta-chip">{{ cleanupLables(managedPaper.gradeName) }}</span>
                <span class="meta-chip">{{ formatSubjectName(managedPaper.subject) }}</span>
                <span v-if="managedPaper.year" class="meta-chip">{{ managedPaper.year }}</span>
            </div>

            <div v-if="managedPaper.files.length === 0" class="empty-state compact">
                <span class="empty-icon"><i class="pi pi-inbox" /></span>
                <span class="empty-title">No files on this paper yet</span>
                <span class="empty-hint">Add the question paper and any tutor-only material below.</span>
            </div>

            <div v-else class="file-groups">
                <section v-for="group in managedFileGroups" :key="group.kind" class="file-group">
                    <div class="file-group-head">
                        <i :class="group.icon" />
                        <span class="file-group-title">{{ group.label }}</span>
                        <span class="file-group-count">{{ group.files.length }}</span>
                    </div>

                    <div v-for="file in group.files" :key="file.fileId" class="managed-file">
                        <i class="pi pi-file-pdf managed-file-icon" />
                        <div class="managed-file-copy">
                            <span class="managed-file-name" :title="file.label || file.fileName">
                                {{ file.label || file.fileName }}
                            </span>
                            <span class="managed-file-sub" v-if="file.sizeBytes">
                                {{ formatFileSize(file.sizeBytes) }}
                            </span>
                        </div>
                        <div class="managed-file-actions">
                            <Button icon="pi pi-download" class="row-action" @click="download(managedPaper, file)"
                                v-tooltip.bottom="'Download'" />
                            <Button icon="pi pi-trash" class="row-action danger" @click="removeFile(file)"
                                v-tooltip.bottom="'Delete file'" />
                        </div>
                    </div>
                </section>
            </div>

            <section class="add-files">
                <div class="section-head">
                    <span class="section-title">Add files</span>
                </div>

                <div class="field">
                    <label class="field-label">Kind</label>
                    <Select v-model="addKind" :options="addKindOptions" optionLabel="label" optionValue="value"
                        class="w-full" size="small" variant="filled" />
                </div>

                <PaperFileDrop ref="addUploadRef" v-model="addFiles" name="files"
                    :title="addKindTitle" :icon="kindMeta(addKind).icon" :maxFileSize="MAX_FILE_SIZE" />

                <small v-if="addKind !== 'paper'" class="field-hint">
                    <i class="pi pi-lock" />
                    {{ addKind === 'answer_sheet' ? 'Answer sheets' : 'Marking schemes' }} are tutor-only and
                    will not be shown to students.
                </small>

                <div class="add-files-actions">
                    <Button label="Upload" icon="pi pi-upload" size="small" class="accent-button"
                        :loading="isAddingFiles" :disabled="addFiles.length === 0" @click="uploadAdditionalFiles" />
                </div>
            </section>
        </div>
    </Dialog>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import PaperFileDrop from '@/components/dashboard/admin/pastPapers/PaperFileDrop.vue'
import { Button, Select } from 'primevue'
import Dialog from 'primevue/dialog';
import { useConfirm } from 'primevue/useconfirm';
import { notifyError, notifySuccess } from '@/utils/notification';
import { addPaperFiles, deletePaperFile } from '@/api/papers';
import { cleanupLables, formatSubjectName } from '@/utils/cleanups';
import { downloadPaperFile, kindMeta, filesOfKind, formatFileSize } from '@/utils/paperFiles';

const props = defineProps({
    visible: { type: Boolean, default: false },
    paper: { type: Object, default: null },
});

const emit = defineEmits(['update:visible', 'updated']);

const confirm = useConfirm();

const manageDialogVisible = computed({
    get: () => props.visible,
    set: (value) => emit('update:visible', value),
});

const managedPaper = computed(() => props.paper);

// Matches the 30 MB multer limit on POST /past-papers/admin.
const MAX_FILE_SIZE = 30000000;

const addKindOptions = [
    { label: 'Past paper', value: 'paper' },
    { label: 'Marking scheme (tutors only)', value: 'marking_scheme' },
    { label: 'Answer sheet (tutors only)', value: 'answer_sheet' },
];

const addKind = ref('answer_sheet');
const addFiles = ref([]);
const addUploadRef = ref(null);
const isAddingFiles = ref(false);

const managedFileGroups = computed(() => {
    const kinds = ['paper', 'marking_scheme', 'answer_sheet'];

    return kinds
        .map(kind => ({
            kind,
            label: kindMeta(kind).label,
            icon: kindMeta(kind).icon,
            files: filesOfKind(managedPaper.value, kind),
        }))
        .filter(group => group.files.length > 0);
});

const addKindTitle = computed(() => `Add ${kindMeta(addKind.value).label.toLowerCase()} PDFs`);

watch(() => props.visible, (visible) => {
    if (!visible) return;
    addKind.value = 'answer_sheet';
    addFiles.value = [];
    addUploadRef.value?.clear();
});

const removeFile = (file) => {
    confirm.require({
        message: `Do you want to delete "${file.label || file.fileName}"?`,
        header: 'Danger Zone',
        icon: 'pi pi-info-circle',
        rejectProps: {
            label: 'Cancel',
            severity: 'secondary',
            outlined: true
        },
        acceptProps: {
            label: 'Delete',
            severity: 'danger'
        },
        accept: async () => {
            try {
                const res = await deletePaperFile(managedPaper.value._id, file.fileId);
                emit('updated', res.data?.data);
                notifySuccess("Deleted!", "The file has been removed.");
            } catch (err) {
                notifyError("Error", err?.response?.data?.message || "Failed to delete the file.");
            }
        },
    });
};

const uploadAdditionalFiles = async () => {
    const fieldByKind = {
        paper: 'paperFiles',
        marking_scheme: 'markingSchemeFiles',
        answer_sheet: 'answerSheetFiles',
    };

    isAddingFiles.value = true;
    try {
        const formData = new FormData();
        addFiles.value.forEach(file => formData.append(fieldByKind[addKind.value], file));

        const res = await addPaperFiles(managedPaper.value._id, formData);
        emit('updated', res.data?.data);

        notifySuccess("Uploaded!", `${addFiles.value.length} file(s) added.`);
        addFiles.value = [];
        addUploadRef.value?.clear();
    } catch (err) {
        notifyError("Error", err?.response?.data?.message || "Failed to upload the files.");
    } finally {
        isAddingFiles.value = false;
    }
};

const download = (paper, file) => downloadPaperFile(paper, file);
</script>

<style scoped>
.manage-body {
    display: flex;
    flex-direction: column;
    gap: 1.1rem;
}

.paper-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.meta-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.28rem;
    padding: 0.14rem 0.42rem;
    border-radius: 6px;
    background-color: var(--gray-200);
    font-size: 0.7rem;
    color: var(--black-700);
}

.section-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.section-title {
    font-size: 0.95rem;
    font-weight: 500;
    color: var(--gray-600);
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
    min-width: 0;
}

.field-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-600);
}

.field-hint {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.7rem;
    line-height: 1.4;
    color: var(--black-700);
}

.field-hint i {
    font-size: 0.68rem;
}

.field :deep(.p-select) {
    width: 100%;
}

/* ---------- Empty state ---------- */

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.45rem;
    padding: 2.5rem 1rem;
    border: 1px dashed var(--black-50);
    border-radius: 12px;
    background-color: #FCFDFE;
    text-align: center;
}

.empty-state.compact {
    padding: 1.5rem 1rem;
}

.empty-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 50%;
    background-color: var(--accent-50);
    color: var(--accent-500);
}

.empty-icon i {
    font-size: 1.2rem;
}

.empty-title {
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--gray-600);
}

.empty-hint {
    max-width: 24rem;
    font-size: 0.75rem;
    line-height: 1.5;
    color: var(--black-700);
}

/* ---------- File groups ---------- */

.file-groups {
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.file-group {
    --group-tint: var(--accent-50);
    --group-accent: var(--accent-500);
    border: 1px solid var(--black-50);
    border-radius: 10px;
    overflow: hidden;
}


.file-group-head {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--black-50);
    background-color: var(--group-tint);
}

.file-group-head>i {
    font-size: 0.8rem;
    color: var(--group-accent);
}

.file-group-title {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-600);
}

.file-group-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.35rem;
    border-radius: 999px;
    background-color: #fff;
    font-size: 0.66rem;
    font-weight: 500;
    color: var(--gray-600);
}

.managed-file {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.55rem 0.75rem;
}

.managed-file+.managed-file {
    border-top: 1px solid var(--black-50);
}

.managed-file-icon {
    flex-shrink: 0;
    font-size: 0.85rem;
    color: var(--group-accent);
}

.managed-file-copy {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    min-width: 0;
    flex: 1;
}

.managed-file-name {
    font-size: 0.82rem;
    color: var(--gray-600);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.managed-file-sub {
    font-size: 0.68rem;
    color: var(--black-700);
}

.managed-file-actions {
    display: flex;
    gap: 0.15rem;
    flex-shrink: 0;
}

.add-files {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding-top: 1rem;
    border-top: 1px dashed var(--black-50);
}

.add-files-actions {
    display: flex;
    justify-content: flex-end;
}

/* ---------- Row actions ---------- */

.row-action.p-button {
    width: 2rem;
    height: 2rem;
    padding: 0;
    border: 0;
    border-radius: 8px;
    background-color: transparent;
    color: var(--black-700);
    transition: background-color 0.15s ease, color 0.15s ease;
}

.row-action.p-button:not(:disabled):hover {
    background-color: var(--accent-50);
    color: var(--accent-700);
}

.row-action.danger.p-button:not(:disabled):hover {
    background-color: #FEE2E2;
    color: #B91C1C;
}

.row-action.p-button:hover,
.row-action.p-button:focus,
.row-action.p-button:active {
    border-color: transparent;
    outline: none;
    box-shadow: none;
}

.row-action.p-button:focus-visible {
    outline: 1px solid var(--black-700);
    outline-offset: 1px;
    box-shadow: none;
}

.row-action :deep(.p-button-icon) {
    font-size: 0.8rem;
}
</style>
